'use client'

import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion'
import { Plus, Pencil, Trash2 } from 'lucide-react'
import type { HelmAuditLog } from '@/lib/database.types'

interface AuditLogTableProps {
  logs: HelmAuditLog[]
}

const actionIcons: Record<string, React.ReactNode> = {
  INSERT: <Plus className="h-3 w-3" />,
  UPDATE: <Pencil className="h-3 w-3" />,
  DELETE: <Trash2 className="h-3 w-3" />,
}

const actionColors: Record<string, string> = {
  INSERT: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  UPDATE: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  DELETE: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
}

function formatFieldName(field: string): string {
  return field
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
}

function formatValue(value: unknown): string {
  if (value === null || value === undefined) return '-'
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  if (typeof value === 'object') return JSON.stringify(value)
  return String(value)
}

function getChangedFields(oldValues: Record<string, unknown> | null, newValues: Record<string, unknown> | null): Array<{
  field: string
  oldValue: unknown
  newValue: unknown
}> {
  if (!oldValues && !newValues) return []

  const changes: Array<{ field: string; oldValue: unknown; newValue: unknown }> = []
  const allKeys = new Set([
    ...Object.keys(oldValues || {}),
    ...Object.keys(newValues || {}),
  ])

  // Skip internal fields
  const skipFields = ['id', 'created_at', 'updated_at', 'created_by']

  for (const key of allKeys) {
    if (skipFields.includes(key)) continue

    const oldVal = oldValues?.[key]
    const newVal = newValues?.[key]

    if (JSON.stringify(oldVal) !== JSON.stringify(newVal)) {
      changes.push({
        field: key,
        oldValue: oldVal,
        newValue: newVal,
      })
    }
  }

  return changes
}

export function AuditLogTable({ logs }: AuditLogTableProps) {
  if (logs.length === 0) {
    return <p className="text-muted-foreground">No activity recorded yet.</p>
  }

  return (
    <div className="space-y-4">
      {logs.map((log) => {
        const changes = getChangedFields(log.old_values, log.new_values)

        return (
          <div key={log.id} className="border rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Badge className={actionColors[log.action] || 'bg-gray-100'}>
                  {actionIcons[log.action]}
                  <span className="ml-1">{log.action}</span>
                </Badge>
                <span className="text-sm text-muted-foreground">
                  {new Date(log.changed_at).toLocaleString()}
                </span>
              </div>
            </div>

            {log.action === 'INSERT' && (
              <p className="text-sm text-muted-foreground">Customer record created</p>
            )}

            {log.action === 'DELETE' && (
              <p className="text-sm text-muted-foreground">Customer record deleted</p>
            )}

            {log.action === 'UPDATE' && changes.length > 0 && (
              <Accordion type="single" collapsible className="w-full">
                <AccordionItem value="changes" className="border-0">
                  <AccordionTrigger className="py-2 text-sm">
                    {changes.length} field{changes.length === 1 ? '' : 's'} changed
                  </AccordionTrigger>
                  <AccordionContent>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-1/3">Field</TableHead>
                          <TableHead className="w-1/3">Previous</TableHead>
                          <TableHead className="w-1/3">New</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {changes.map((change) => (
                          <TableRow key={change.field}>
                            <TableCell className="font-medium">
                              {formatFieldName(change.field)}
                            </TableCell>
                            <TableCell className="text-muted-foreground">
                              {formatValue(change.oldValue)}
                            </TableCell>
                            <TableCell>{formatValue(change.newValue)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            )}

            {log.action === 'UPDATE' && changes.length === 0 && (
              <p className="text-sm text-muted-foreground">No visible field changes</p>
            )}
          </div>
        )
      })}
    </div>
  )
}
